import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Storage } from 'aws-amplify'
import { getLogger } from '@/shared/utils'
import { useCognitoAuth } from '@/app/modules/auth'
import { S3ImageModal } from './S3ImageModal'
import {
  deletePresignedUrl,
  getPresignedUrl,
  setPresignedUrl,
  subscribePresignedUrlEvictions,
} from './presignedUrlCache'

const logger = getLogger()

interface S3ImageProps {
  s3Key: string
  alt?: string
  maxHeight?: number
}

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 3000

/**
 * Renders a thumbnail of an image stored in the user's private S3 bucket.
 * Fetches a presigned URL via Amplify Storage, caches it per identity + key, and displays a
 * clickable thumbnail that opens a full-size lightbox on click.
 * Retries on load failure to handle race conditions where the agent just
 * saved the file and S3 hasn't fully propagated yet. A turn that changes the file evicts the
 * cached URL, and a mounted image presigns again so it shows the new content.
 */
export const S3Image: React.FC<S3ImageProps> = ({ s3Key, alt = 'Generated image', maxHeight = 200 }) => {
  const identityId = useCognitoAuth().currentUser?.identity_id ?? ''
  const [url, setUrl] = useState<string | null>(() => getPresignedUrl(identityId, s3Key) ?? null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(url ? 'ready' : 'loading')
  const [modalOpen, setModalOpen] = useState(false)
  const mountedRef = useRef(true)
  const retryCountRef = useRef(0)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const fetchPresignedUrl = useCallback((bustCache: boolean = false) => {
    const cached = bustCache ? undefined : getPresignedUrl(identityId, s3Key)
    if (cached !== undefined) {
      setUrl(cached)
      setStatus('ready')
      return
    }

    setStatus('loading')

    Storage.get(s3Key, {
      customPrefix: { public: '', private: '', protected: '' },
      level: 'private',
      download: false,
    })
      .then((presignedUrl) => {
        if (!mountedRef.current) return
        const urlStr = typeof presignedUrl === 'string' ? presignedUrl : ''
        logger.debug('[S3Image] presigned URL for key=%s → %s', s3Key, urlStr.slice(0, 120))
        setPresignedUrl(identityId, s3Key, urlStr)
        setUrl(urlStr)
        setStatus('ready')
      })
      .catch((err) => {
        logger.error('[S3Image] Storage.get failed for key=%s', s3Key, err)
        if (!mountedRef.current) return
        // Presigning needs live Cognito credentials, so this fails transiently on a tab returning
        // from hours idle — exactly when a cloud continuation's results are first read. Retry it
        // like an <img> failure instead of leaving the image permanently broken until a reload.
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1
          setTimeout(() => {
            if (mountedRef.current) fetchPresignedUrl(true)
          }, RETRY_DELAY_MS)
          return
        }
        setStatus('error')
      })
  }, [identityId, s3Key])

  useEffect(() => {
    retryCountRef.current = 0
    fetchPresignedUrl()
  }, [fetchPresignedUrl])

  useEffect(
    () =>
      subscribePresignedUrlEvictions((isEvicted) => {
        if (!isEvicted(s3Key)) return
        retryCountRef.current = 0
        fetchPresignedUrl(true)
      }),
    [s3Key, fetchPresignedUrl]
  )

  const handleImgError = useCallback(() => {
    logger.warn('[S3Image] <img> load failed for key=%s (retry %d/%d)', s3Key, retryCountRef.current + 1, MAX_RETRIES)
    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1
      deletePresignedUrl(identityId, s3Key)
      setTimeout(() => {
        if (mountedRef.current) fetchPresignedUrl(true)
      }, RETRY_DELAY_MS)
    } else {
      setStatus('error')
    }
  }, [identityId, s3Key, fetchPresignedUrl])

  const handleRetry = useCallback(() => {
    retryCountRef.current = 0
    deletePresignedUrl(identityId, s3Key)
    fetchPresignedUrl(true)
  }, [identityId, s3Key, fetchPresignedUrl])

  const handleClick = useCallback(() => setModalOpen(true), [])
  const handleClose = useCallback(() => setModalOpen(false), [])

  if (status === 'loading') {
    return (
      <div className='s3-image-thumb s3-image-thumb--loading' style={{ maxHeight }}>
        <i className='fas fa-image' aria-hidden />
        <span>{retryCountRef.current > 0 ? 'Retrying...' : 'Loading image...'}</span>
      </div>
    )
  }

  if (status === 'error' || !url) {
    return (
      <div className='s3-image-thumb s3-image-thumb--error'>
        <i className='fas fa-triangle-exclamation' aria-hidden />
        <span>Could not load image</span>
        <span className='s3-image-thumb__key' title={s3Key}>{s3Key}</span>
        {/* The file is usually there and the credentials were merely stale; without this the only
            way back is reloading the whole page. */}
        <button type='button' className='s3-image-thumb__retry' onClick={handleRetry}>
          Try again
        </button>
      </div>
    )
  }

  return (
    <>
      <div
        className='s3-image-thumb'
        role='button'
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
      >
        <img src={url} alt={alt} style={{ maxHeight }} onError={handleImgError} />
      </div>
      {modalOpen && <S3ImageModal url={url} alt={alt} onClose={handleClose} />}
    </>
  )
}
